import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import TopBar from "@/components/TopBar";
import { api } from "@/lib/api";
import { StickyNote, Plus, Trash2, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import EmptyState from "@/components/EmptyState";
import FavPinButtons from "@/components/FavPinButtons";
import RowActions from "@/components/RowActions";
import SwipeableRow from "@/components/SwipeableRow";
import { toast } from "sonner";
import VoiceInput from "@/components/VoiceInput";

import { swallow } from "@/lib/log";
export default function Notes() {
  const [items, setItems] = useState([]);
  const [households, setHouseholds] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [shareOpen, setShareOpen] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/notes"); setItems(data); } catch (e) { swallow("Notes")(e); }
    try { const { data } = await api.get("/households"); setHouseholds(data); } catch (e) { swallow("Notes")(e); }
  };
  useEffect(() => { load(); }, []);

  // Contextual FAB deep-link: `?new=1` opens the new-note dialog
  const [sp, setSp] = useSearchParams();
  useEffect(() => {
    if (sp.get("new") === "1") {
      openNew();
      const next = new URLSearchParams(sp);
      next.delete("new");
      setSp(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp]);

  const openNew = () => { setEditing(null); setTitle(""); setContent(""); setOpen(true); };
  const openEdit = (n) => { setEditing(n); setTitle(n.title || ""); setContent(n.content || ""); setOpen(true); };

  const save = async () => {
    if (!content.trim()) { toast.error("Note content required"); return; }
    try {
      let saved;
      if (editing) {
        const { data } = await api.put(`/notes/${editing.id}`, { title, content });
        saved = data;
      } else {
        const { data } = await api.post("/notes", { title: title || null, content });
        saved = data;
      }
      // Optimistic list update — the SW read cache may briefly serve a stale
      // snapshot on the reload below, so we surface the write immediately.
      if (saved && saved.id) {
        setItems((prev) => {
          if (editing) return prev.map((it) => it.id === saved.id ? saved : it);
          return prev.some((x) => x.id === saved.id) ? prev : [saved, ...prev];
        });
      }
      setOpen(false);
      toast.success(editing ? "Note updated" : "Note saved");
      load(); // background reconciliation
    } catch { toast.error("Failed to save"); }
  };

  // NOTE: RowActions already renders the "Delete note — Undo" toast and only
  // calls this after the 5s countdown elapses without Undo. So we just do the
  // real API delete + optimistic UI removal here — no extra toast/timeout.
  const del = async (n) => {
    setItems((arr) => arr.filter((it) => it.id !== n.id));
    try { await api.delete(`/notes/${n.id}`); }
    catch { toast.error("Delete failed — restored"); load(); }
  };

  return (
    <>
      <TopBar title="Notes" subtitle="Ideas, lists, memory" back showSearch
        right={
          <button onClick={openNew} className="h-9 w-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center" data-testid="notes-add-button">
            <Plus className="h-4 w-4" />
          </button>
        }
      />
      <div className="px-4 py-4 space-y-3">
        {items.length === 0 && (
          <EmptyState icon={StickyNote} title="No notes yet" subtitle="Tap + to create one, or say 'Save a note...' in chat." />
        )}
        {items.map((n) => (
          <SwipeableRow
            key={n.id}
            testId={`swipe-note-${n.id}`}
            onSwipeLeft={async () => {
              // Optimistic archive
              setItems((arr) => arr.filter((it) => it.id !== n.id));
              try {
                await api.post("/archive/toggle", { type: "note", id: n.id, archived: true });
                toast.success("Note archived");
              } catch { toast.error("Archive failed"); load(); }
            }}
            onSwipeRight={async () => {
              const next = !n.is_favorite;
              setItems((arr) => arr.map((it) => it.id === n.id ? { ...it, is_favorite: next } : it));
              try {
                await api.post("/favorites/toggle", { type: "note", id: n.id, is_favorite: next });
                toast.success(next ? "Added to Favorites" : "Removed from Favorites");
              } catch { toast.error("Update failed"); load(); }
            }}
          >
            <div className="rounded-2xl border border-border bg-card p-4" data-testid="note-item">
              <div className="flex items-start justify-between gap-2">
                <button onClick={() => openEdit(n)} className="text-left flex-1">
                  {n.title && <div className="font-display font-semibold text-sm mb-1">{n.title}</div>}
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap line-clamp-4">{n.content}</p>
                  {(n.shared_with_households || []).length > 0 && (
                    <div className="mt-1 text-[10px] text-primary inline-flex items-center gap-1">
                      <Share2 className="h-3 w-3" /> Shared with {n.shared_with_households.length} household{n.shared_with_households.length !== 1 ? "s" : ""}
                    </div>
                  )}
                </button>
                <div className="flex items-center gap-1 shrink-0">
                  <FavPinButtons
                    type="note"
                    id={n.id}
                    isFavorite={!!n.is_favorite}
                    pinnedAt={n.pinned_at}
                    size="sm"
                    onChanged={(u) =>
                      setItems((arr) => arr.map((it) => it.id === n.id ? { ...it, ...u } : it))
                    }
                    onArchived={(id) => setItems((arr) => arr.filter((it) => it.id !== id))}
                  />
                  <RowActions
                    size="sm"
                    testId={`note-${n.id}`}
                    deleteLabel="Delete note"
                    onShare={households.length > 0 ? () => setShareOpen(n.id) : null}
                    onDelete={() => del(n)}
                  />
                </div>
              </div>
            </div>
          </SwipeableRow>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit note" : "New note"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="note-title-input" />
            <div className="relative">
              <Textarea rows={6} placeholder="Write your note…" value={content} onChange={(e) => setContent(e.target.value)} data-testid="note-content-input" />
              <div className="absolute right-2 bottom-2">
                <VoiceInput size="sm" onTranscript={(t) => setContent(t)} testId="note-voice-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="note-save-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!shareOpen} onOpenChange={(v) => !v && setShareOpen(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-display">Share with household</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {households.map((h) => (
              <button key={h.id} onClick={async () => {
                try { await api.post("/households/share", { type: "note", id: shareOpen, household_id: h.id }); toast.success("Shared"); setShareOpen(null); load(); }
                catch { toast.error("Share failed"); }
              }} className="w-full text-left rounded-xl border border-border bg-card p-3 hover:bg-[hsl(var(--accent))]" data-testid={`share-note-with-${h.id}`}>
                <div className="text-sm font-medium">{h.name}</div>
                <div className="text-xs text-muted-foreground">{h.members.length} member{h.members.length !== 1 ? "s" : ""}</div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
